import {
  Alert,
  Chip,
  Collapse,
  Icon,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useCallback, useState } from "react";
import { capitalCase } from "change-case";

const STATUS_COLORS: Record<string, "success" | "error" | "warning" | "default"> = {
  success: "success",
  failed: "error",
  retrying: "warning",
  pending: "default",
};

function StatusChip({ status }: { status: string }) {
  return (
    <Chip label={capitalCase(status)} size="small" color={STATUS_COLORS[status] ?? "default"} />
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);
  return (
    <Tooltip title={copied ? "Copied!" : "Copy"}>
      <IconButton size="small" onClick={handleCopy}>
        <Icon fontSize="small">{copied ? "check" : "content_copy"}</Icon>
      </IconButton>
    </Tooltip>
  );
}

export default function ApiTriggerDeliveryRow({ delivery }: { delivery: any }) {
  const [open, setOpen] = useState(false);

  const detailObj = {
    uuid: delivery.uuid,
    eventType: delivery.eventType,
    status: delivery.status,
    attempts: delivery.attempts,
    httpStatusCode: delivery.httpStatusCode ?? null,
    errorMessage: delivery.errorMessage ?? null,
    deliveredAt: delivery.deliveredAt ?? null,
    nextRetryAt: delivery.nextRetryAt ?? null,
    createdAt: delivery.createdAt,
  };

  const detailJson = JSON.stringify(detailObj, null, 2);

  return (
    <>
      <TableRow hover sx={open ? { "& > *": { borderBottom: "unset" } } : undefined}>
        <TableCell sx={{ width: 32, p: "4px 8px" }}>
          <IconButton size="small" onClick={() => setOpen((v) => !v)}>
            <Icon fontSize="small">{open ? "expand_less" : "expand_more"}</Icon>
          </IconButton>
        </TableCell>
        <TableCell>
          <StatusChip status={delivery.status} />
        </TableCell>
        <TableCell>
          <MDTypography variant="caption">{capitalCase(delivery.eventType)}</MDTypography>
        </TableCell>
        <TableCell>
          {delivery.httpStatusCode ? (
            <Chip
              label={delivery.httpStatusCode}
              size="small"
              color={
                delivery.httpStatusCode >= 200 && delivery.httpStatusCode < 300
                  ? "success"
                  : "error"
              }
              variant="outlined"
            />
          ) : (
            <MDTypography variant="caption" color="text">
              —
            </MDTypography>
          )}
        </TableCell>
        <TableCell>
          <MDTypography variant="caption">{delivery.attempts}</MDTypography>
        </TableCell>
        <TableCell>
          <MDTypography variant="caption">
            {delivery.deliveredAt ? new Date(delivery.deliveredAt).toLocaleString() : "—"}
          </MDTypography>
        </TableCell>
        <TableCell>
          <MDTypography variant="caption">
            {new Date(delivery.createdAt).toLocaleString()}
          </MDTypography>
        </TableCell>
        <TableCell sx={{ p: "4px 8px", whiteSpace: "nowrap" }}>
          <CopyButton text={detailJson} />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={8} sx={{ p: 0, ...(open ? {} : { border: 0 }) }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <MDBox px={3} py={2} sx={{ background: "background.default" }}>
              <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                <MDTypography variant="caption" fontWeight="medium">
                  Delivery details
                </MDTypography>
                <CopyButton text={detailJson} />
              </MDBox>
              <MDBox
                component="pre"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "11px",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1.5,
                  m: 0,
                  overflowX: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {detailJson}
              </MDBox>
              {delivery.errorMessage && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {delivery.errorMessage}
                </Alert>
              )}
              {delivery.nextRetryAt && (
                <MDTypography variant="caption" color="text" display="block" mt={1}>
                  Next retry: {new Date(delivery.nextRetryAt).toLocaleString()}
                </MDTypography>
              )}
            </MDBox>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
